import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { PAGE_SIZE_DIMENSIONS, PAGE_SIZE_A4 } from '../../../../utilities/constants/pageSizeConstants';
import { Position } from 'reactflow';
import { getBusLayout, getBusHandleAnchor } from '../../../../utilities/busLayout';

// Render cross-page edge labels inside the viewport so they follow transforms
const EdgeCrossPageOverlay = ({ transform }) => {
  const pageSize = useSelector((s) => s.view.pageSize);
  const { width, height } = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
  const nodes = useSelector((s) => s.node.nodes) || [];
  const edges = useSelector((s) => s.edge.edges) || [];
  const gridGapPx = useSelector((s) => s.view.gridGapPx) || 25;

  const [viewportEl, setViewportEl] = useState(null);
  useEffect(() => {
    const el = document.querySelector('.react-flow__viewport');
    setViewportEl(el || null);
  }, []);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const centerOf = (n) => ({ x: (n?.position?.x || 0) + ((n?.width) || 0) / 2, y: (n?.position?.y || 0) + ((n?.height) || 0) / 2 });
  const tileOf = (pt) => ({ ix: Math.floor(pt.x / width), iy: Math.floor(pt.y / height) });

  // Deterministic page labels by coordinate order (A, B, C...)
  const toAlpha = (n) => {
    const A = 'A'.charCodeAt(0);
    let s = '';
    n = Math.max(0, n);
    do { s = String.fromCharCode(A + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
    return s;
  };

  const alphaToNumber = (label) => {
    if (!label || typeof label !== 'string') return 0;
    const chars = label.toUpperCase().split('');
    return chars.reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0);
  };

  const pageLabels = useMemo(() => {
    const keys = new Set();
    edges.forEach((e) => {
      const sn = byId.get(e.source); const tn = byId.get(e.target);
      if (!sn || !tn) return;
      const sp = centerOf(sn); const tp = centerOf(tn);
      const sT = tileOf(sp); const tT = tileOf(tp);
      if (sT.ix === tT.ix && sT.iy === tT.iy) return;
      keys.add(`${sT.ix},${sT.iy}`);
      keys.add(`${tT.ix},${tT.iy}`);
    });
    const sorted = Array.from(keys).map(k => ({ k, ix: Number(k.split(',')[0]), iy: Number(k.split(',')[1]) }))
      .sort((a, b) => (a.ix !== b.ix ? a.ix - b.ix : a.iy - b.iy));
    const map = {};
    sorted.forEach((entry, idx) => { map[entry.k] = toAlpha(idx); });
    return map;
  }, [edges, byId, width, height]);

  const items = useMemo(() => {
    const out = [];
    const inset = 10; // keep label inside source page
    const mergeCollinear = (arr) => {
      if (!arr || arr.length <= 2) return arr || [];
      const res = [arr[0]];
      for (let i = 1; i < arr.length - 1; i++) {
        const a = res[res.length - 1];
        const b = arr[i];
        const c = arr[i + 1];
        const colX = a.x === b.x && b.x === c.x;
        const colY = a.y === b.y && b.y === c.y;
        if (colX || colY) continue;
        res.push(b);
      }
      res.push(arr[arr.length - 1]);
      return res;
    };
    const busLayoutCache = new Map();
    const getLayoutForNode = (node) => {
      if (!node || node.type !== 'busNode') return null;
      if (busLayoutCache.has(node.id)) return busLayoutCache.get(node.id);
      const layout = getBusLayout(node, gridGapPx);
      busLayoutCache.set(node.id, layout);
      return layout;
    };

    // helper to compute connector anchor on a node similar to edge renderers
    const calcAnchor = (node, role, handleId) => {
      if (!node) return null;
      const x = (node.position?.x ?? 0);
      const y = (node.position?.y ?? 0);
      const w = node.width || 0;
      const h = node.height || 0;
      const ang = typeof node?.data?.angle === 'number' ? node.data.angle : 0; // 0 = vertical layout
      // Bus nodes: compute handle distribution
      if (node?.type === 'busNode') {
        const layout = getLayoutForNode(node);
        if (layout) {
          const roleKey = role === 'source' ? 'source' : 'target';
          return getBusHandleAnchor(node, layout, roleKey, handleId);
        }
      }
      // Non-bus with no handles list: side centers based on angle
      const list = Array.isArray(node?.data?.handles) ? node.data.handles : null;
      if (!list || list.length === 0) {
        const vertical = ang === 0;
        if (vertical) return role === 'source' ? { x: x + w / 2, y: y + h } : { x: x + w / 2, y };
        return role === 'source' ? { x: x + w, y: y + h / 2 } : { x, y: y + h / 2 };
      }
      // With handles list: choose the exact handle if known else the lone handle for role
      try {
        const chosen = list.find((h) => String(h?.id) === String(handleId)) || list.find((h) => (h?.type || '').toLowerCase() === (role || '').toLowerCase());
        if (!chosen) return null;
        const mapPos = (pos) => {
          if (ang === 0) return pos;
          switch (pos) {
            case Position.Top: return Position.Left;
            case Position.Bottom: return Position.Right;
            case Position.Left: return Position.Top;
            case Position.Right: return Position.Bottom;
            default: return pos;
          }
        };
        const mapped = mapPos(chosen.position);
        const style = chosen?.style || {};
        const pickPercent = (val, total) => {
          if (typeof val === 'number') return Math.max(0, Math.min(1, total ? val / total : 0));
          const s = String(val || '').trim();
          if (s.endsWith('%')) { const p = parseFloat(s.slice(0, -1)); if (!isNaN(p)) return Math.max(0, Math.min(1, p / 100)); }
          return 0.5;
        };
        const frac = (chosen.position === Position.Top || chosen.position === Position.Bottom)
          ? pickPercent(style.left, w)
          : pickPercent(style.top, h);
        switch (mapped) {
          case Position.Top: return { x: x + w * frac, y };
          case Position.Bottom: return { x: x + w * frac, y: y + h };
          case Position.Left: return { x, y: y + h * frac };
          case Position.Right: return { x: x + w, y: y + h * frac };
          default: return null;
        }
      } catch {}
      return null;
    };

    edges.forEach((e) => {
      const sn = byId.get(e.source); const tn = byId.get(e.target);
      if (!sn || !tn) return;
      const sp = calcAnchor(sn, 'source', e.sourceHandle) || centerOf(sn);
      const tp = calcAnchor(tn, 'target', e.targetHandle) || centerOf(tn);
      const sT = tileOf(sp); const tT = tileOf(tp);
      if (sT.ix === tT.ix && sT.iy === tT.iy) return;
      const tKey = `${tT.ix},${tT.iy}`;
      const lT = pageLabels[tKey] || '?';
      // Compute the exact crossing point of the edge polyline with the source tile border
      // Include lead-out/in segments for bus nodes to match rendered geometry
      const mkLead = (node, anchor, role) => {
        if (!node || node.type !== 'busNode') return null;
        const layout = getLayoutForNode(node);
        if (!layout) return null;
        const lead = Math.max(10, layout.thickness + 6);
        if (layout.horizontal) {
          // horizontal bus: source exits bottom, target enters top
          return role === 'source' ? { x: anchor.x, y: anchor.y + lead } : { x: anchor.x, y: anchor.y - lead };
        }
        // vertical bus: source exits right, target enters left
        return role === 'source' ? { x: anchor.x + lead, y: anchor.y } : { x: anchor.x - lead, y: anchor.y };
      };
      const sLead = mkLead(sn, sp, 'source');
      const tLead = mkLead(tn, tp, 'target');
      let points = [sp, ...(sLead ? [sLead] : []), ...((e?.data?.waypoints) || []), ...(tLead ? [tLead] : []), tp];
      if ((e?.type || '').toLowerCase() === 'orthogonaledge') {
        if (points.length === 2) {
          const mid = { x: sp.x, y: tp.y };
          points = mergeCollinear([sp, mid, tp]);
        } else {
          const outPts = [points[0]];
          for (let i = 1; i < points.length; i++) {
            const a = outPts[outPts.length - 1];
            const b = points[i];
            if (a.x !== b.x && a.y !== b.y) {
              outPts.push({ x: a.x, y: b.y });
            }
            outPts.push(b);
          }
          points = mergeCollinear(outPts);
        }
      }
      const rect = {
        l: sT.ix * width,
        r: (sT.ix + 1) * width,
        t: sT.iy * height,
        b: (sT.iy + 1) * height,
      };
      const eps = 1e-6;
      const inside = (p) => p.x >= rect.l - eps && p.x <= rect.r + eps && p.y >= rect.t - eps && p.y <= rect.b + eps;
      const segHit = () => {
        for (let i = 0; i < points.length - 1; i++) {
          const p0 = points[i];
          const p1 = points[i + 1];
          const in0 = inside(p0);
          const in1 = inside(p1);
          if (in0 && !in1) {
            const hits = [];
            if (Math.abs(p1.x - p0.x) > eps) {
              const tL = (rect.l - p0.x) / (p1.x - p0.x);
              const yL = p0.y + tL * (p1.y - p0.y);
              if (tL >= 0 && tL <= 1 && yL >= rect.t - eps && yL <= rect.b + eps) hits.push({ t: tL, x: rect.l, y: yL, side: 'left' });
              const tR = (rect.r - p0.x) / (p1.x - p0.x);
              const yR = p0.y + tR * (p1.y - p0.y);
              if (tR >= 0 && tR <= 1 && yR >= rect.t - eps && yR <= rect.b + eps) hits.push({ t: tR, x: rect.r, y: yR, side: 'right' });
            }
            if (Math.abs(p1.y - p0.y) > eps) {
              const tT = (rect.t - p0.y) / (p1.y - p0.y);
              const xT = p0.x + tT * (p1.x - p0.x);
              if (tT >= 0 && tT <= 1 && xT >= rect.l - eps && xT <= rect.r + eps) hits.push({ t: tT, x: xT, y: rect.t, side: 'top' });
              const tB = (rect.b - p0.y) / (p1.y - p0.y);
              const xB = p0.x + tB * (p1.x - p0.x);
              if (tB >= 0 && tB <= 1 && xB >= rect.l - eps && xB <= rect.r + eps) hits.push({ t: tB, x: xB, y: rect.b, side: 'bottom' });
            }
            if (hits.length) {
              hits.sort((a, b) => a.t - b.t);
              return hits[0];
            }
          }
        }
        return null;
      };
      const hit = segHit();
      if (!hit) return;
      // Position the label near the crossing point, staying on the source page.
      // - Vertical page border (left/right): place the text to the right of the border so it never sits over the line.
      //   Center vertically on the crossing y, with a 10px horizontal gap from the border.
      // - Horizontal page border (top/bottom): center horizontally at the crossing x and offset 10px toward the inside.
      let left, top, transform;
      switch (hit.side) {
        case 'right': // vertical border: put text to the right of the line
          left = hit.x + inset; top = hit.y; transform = 'translate(-120%, 50%)'; break;
        case 'left': // vertical border: also put text to the right of the line
          left = hit.x + inset; top = hit.y; transform = 'translate(0, -170%)'; break;
        case 'bottom': // exiting downward -> keep above the border
          left = hit.x; top = hit.y - inset; transform = 'translate(-120%, -150%)'; break;
        case 'top': // exiting upward -> keep below the border (inside)
        default:
          left = hit.x; top = hit.y + inset; transform = 'translate(-50%, 0)'; break;
      }
      // Clamp within source tile for vertical position; for horizontal clamp only when not forcing right-of-line across border
      const tileLeft = sT.ix * width; const tileTop = sT.iy * height;
      if (hit.side === 'left') {
        // Source page is to the right of the border; clamping horizontally keeps text inside source page
        left = Math.max(tileLeft + inset, Math.min(tileLeft + width - inset, left));
      }
      // Always clamp vertical position to stay near the source tile area
      top = Math.max(tileTop + inset, Math.min(tileTop + height - inset, top));
      const text = `Continua en página ${alphaToNumber(lT) || lT}`;
      out.push({ tile: sT, absLeft: left, absTop: top, transform, text, anchorX: hit.x, anchorY: hit.y });
    });
    return out;
  }, [edges, byId, pageLabels, width, height]);

  const overlay = (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
      {items.map((it, idx) => (
        <div
          key={idx}
          className="crosspage-label"
          style={{
            position: 'absolute',
            left: it.absLeft,
            top: it.absTop,
            transform: it.transform,
            color: '#000', opacity: 0.2, fontSize: 17, fontWeight: 900,
            whiteSpace: 'nowrap',
            zIndex: 5
          }}
        >
          {it.text}
        </div>
      ))}
      {items.map((it, idx) => (
        <div
          key={`dot-${idx}`}
          style={{
            position: 'absolute',
            left: it.anchorX,
            top: it.anchorY,
            width: 10,
            height: 10,
            background: 'red',
            opacity:0,
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
          title={`x:${Math.round(it.anchorX)}, y:${Math.round(it.anchorY)}`}
        />
      ))}
    </div>
  );

  return viewportEl ? createPortal(overlay, viewportEl) : null;
};

export default EdgeCrossPageOverlay;
